import type { IconName } from '@zapier/design-system';

//////////////////////////////////////////////////////////////////////
// Link typings
//////////////////////////////////////////////////////////////////////

export type UniversalNavLinkType = {
  bottomLinks?: Array<UniversalNavLinkType>;
  description?: string;
  href: string | null;
  icon?: IconName;
  isExternal?: boolean;
  promoArea?: Array<ArticleStubType | PromoLinksListType>;
  submenu?: Array<UniversalNavLinkType>;
  target?: string;
  text: string;
};

//////////////////////////////////////////////////////////////////////
// Sidebar links
//////////////////////////////////////////////////////////////////////

export const dashboardLink: UniversalNavLinkType = {
  href: '/app/dashboard',
  icon: 'navDashboard' as 'navDashboard',
  text: 'Dashboard',
};

export const zapsLink: UniversalNavLinkType = {
  href: '/app/zaps',
  icon: 'miscBoltAlt' as 'miscBoltAlt',
  text: 'Zaps',
};

export const myAppsLink: UniversalNavLinkType = {
  href: '/app/connections',
  icon: 'navApps' as 'navApps',
  text: 'My Apps',
};

export const historyLink: UniversalNavLinkType = {
  href: '/app/history/usage',
  icon: 'miscClock' as 'miscClock',
  text: 'Zap History',
};

export const exploreLinkInternal: UniversalNavLinkType = {
  href: '/explore',
  icon: 'navExplore' as 'navExplore',
  text: 'Explore',
};

export const helpLink: UniversalNavLinkType = {
  href: '/help',
  icon: 'navHelp' as 'navHelp',
  isExternal: true,
  text: 'Get Help',
};

export const usageLink: UniversalNavLinkType = {
  href: '/app/settings/billing',
  icon: 'miscWallet' as 'miscWallet',
  text: 'Usage',
};

export const transferLink: UniversalNavLinkType = {
  href: '/app/transfers',
  icon: 'arrowConnect' as 'arrowConnect',
  text: 'Transfers',
};
//////////////////////////////////////////////////////////////////////
// Account menu links
//////////////////////////////////////////////////////////////////////

export const moveZapsLink: UniversalNavLinkType = {
  href: '/app/move-zaps/',
  icon: 'arrowBigRight' as 'arrowBigRight',
  text: 'Move Zaps',
};

export const settingsLink: UniversalNavLinkType = {
  href: '/app/settings/profile',
  icon: 'navCog' as 'navCog',
  text: 'Settings',
};

export const logOutLink: UniversalNavLinkType = {
  href: '/logout',
  icon: 'arrowExit' as 'arrowExit',
  text: 'Log Out',
};

//////////////////////////////////////////////////////////////////////
// Groups of links
//////////////////////////////////////////////////////////////////////

export const UniversalSidebarLinks = (
  sideNavData: Array<UniversalNavLinkType>
): Array<UniversalNavLinkType> => [
  dashboardLink,
  zapsLink,
  transferLink,
  ...sideNavData,
  myAppsLink,
  historyLink,
  exploreLinkInternal,
  helpLink,
];

export const AccountsMenuLinks: Array<UniversalNavLinkType> = [
  // `moveZapsLink` conditionally rendered.
  settingsLink,
  logOutLink,
];

//////////////////////////////////////////////////////////////////////
// Dynamic Topnav Data
//////////////////////////////////////////////////////////////////////

export type ArticleStubType = {
  type: string;
  text: UniversalNavLinkType['text'];
  href: UniversalNavLinkType['href'];
  imageUrl: string;
  description: UniversalNavLinkType['description'];
  altText: string;
  target?: UniversalNavLinkType['target'];
};

export type PromoLinksListType = {
  type: string;
  text?: UniversalNavLinkType['text'];
  links: Array<UniversalNavLinkType>;
};

export type CallToAction = {
  href: string;
  text: string;
};

export type TopBarLinksType = {
  callToAction?: CallToAction;
  topBarMenu?: Array<UniversalNavLinkType>;
};

export const topNavFallbackData: TopBarLinksType = {
  topBarMenu: [
    {
      text: 'Solutions',
      href: null,
      submenu: [
        {
          text: 'By role',
          href: null,
          icon: 'personOne',
          submenu: [
            {
              text: 'Marketing',
              href: '/solutions/marketing',
            },
            {
              text: 'Business owners',
              href: '/solutions/business-owner',
            },
            {
              text: 'IT',
              href: '/solutions/it',
            },
            {
              text: 'Sales operations',
              href: '/solutions/sales',
            },
          ],
        },
        {
          text: 'By workflow',
          href: null,
          icon: 'navBriefcase',
          submenu: [
            {
              text: 'Lead management',
              href: '/solutions/lead-management',
            },
            {
              text: 'Customer communications',
              href: '/solutions/customer-communication',
            },
            {
              text: 'Internal processes',
              href: '/solutions/internal-processes',
            },
            {
              text: 'Data management',
              href: '/solutions/data-management',
            },
          ],
        },
        {
          text: 'By company size',
          href: null,
          icon: 'navExplore',
          submenu: [
            {
              text: 'Startups',
              href: '/solutions/startups',
            },
            {
              text: 'Small & medium businesses',
              href: '/solutions/smb',
            },
            {
              text: 'Enterprise',
              href: '/solutions/enterprise',
            },
          ],
        },
      ],
      bottomLinks: [
        {
          text: 'Contact Sales',
          icon: 'actionCalendar',
          href: '/l/contact-sales?demo_source=cs_nav_menu_solutions',
        },
        {
          text: 'Explore app integrations',
          icon: 'actionExplore',
          href: '/apps',
        },
      ],
      promoArea: [
        {
          type: 'article-stub',
          text: 'Streamline team comms',
          href: '/customer-stories/calendly',
          imageUrl:
            'https://cdn.zapier.com/storage/photos/20cbb6de7db26e259a378a859c8ea53b.png',
          description: "How automation saves Calendly's team 10+ hours a week",
          altText: "Calendly's logo.",
        },
        {
          type: 'article-stub',
          text: 'Drive growth—at scale',
          href: '/customer-stories/fellow',
          imageUrl:
            'https://cdn.zapier.com/storage/photos/b11de6045680c6b971d4b7ebeda65d16.png',
          description: 'How Fellow onboards wholesale customers 4x faster',
          altText:
            'Close-up of a person sitting at a desk in front of an open laptop and looking off to the side at a document. Natural light is streaming through the windows in the background.',
        },
      ],
    },
    {
      text: 'Resources & Support',
      href: null,
      submenu: [
        {
          text: 'By role',
          href: null,
          icon: 'fileGuide',
          submenu: [
            {
              text: 'Marketing',
              href: '/resources/marketing',
            },
            {
              text: 'Business owners',
              href: '/resources/business-owner',
            },
            {
              text: 'IT',
              href: '/resources/it',
            },
            {
              text: 'Sales operations',
              href: '/resources/sales',
            },
          ],
        },
        {
          text: 'Learn more',
          href: null,
          icon: 'miscLearn',
          submenu: [
            {
              text: 'Blog',
              href: '/blog/',
            },
            {
              text: 'Zapier University',
              href: '/university',
            },
            {
              text: 'Webinars',
              href: '/resources/webinar',
            },
            {
              text: 'Customer stories',
              href: '/customer-stories',
            },
          ],
        },
        {
          text: 'Get help',
          href: null,
          icon: 'navHelp',
          submenu: [
            {
              text: 'Help Center',
              href: '/help',
            },
            {
              text: 'Community',
              href: 'https://community.zapier.com/',
            },
            {
              text: 'Hire an Expert',
              href: '/experts',
            },
            {
              text: 'Support Services',
              href: '/l/support-services',
            },
            {
              text: 'Contact Support',
              href: '/get-help',
            },
          ],
        },
      ],
      bottomLinks: [
        {
          text: 'Contact Sales',
          icon: 'actionCalendar',
          href: '/l/contact-sales?demo_source=cs_nav_menu_resources',
        },
        {
          text: 'Explore app integrations',
          icon: 'actionExplore',
          href: '/apps',
        },
      ],
      promoArea: [
        {
          type: 'article-stub',
          text: 'Zapier quick-start guide',
          href: '/learn/zapier-quick-start-guide',
          imageUrl:
            'https://cdn.zapier.com/storage/photos/8d754b5e8481fdd190dd7b1cf7738a8d.png',
          description: 'Create your first Zap with ease',
          altText:
            "A simplified user interface design representing a portion of Zapier's platform. There's a vertical menu of platform options. At the top is a dominant orange button with text inside that reads \"Create Zap.\" Beneath the button is a stack of menu icons to represent Zapier's dashboard and zaps.",
        },
        {
          type: 'link-list',
          text: 'Developer resources',
          href: null,
          links: [
            {
              text: 'Build an integration',
              href: '/platform/how-to-build',
            },
            {
              text: 'Documentation',
              href: 'https://platform.zapier.com/docs/zapier-intro',
            },
            {
              text: 'Embedded integrations',
              href: '/partner/solutions',
            },
            {
              text: 'Integration Partner Program',
              href: '/platform/partner-program',
            },
          ],
        },
      ],
    },
    {
      text: 'Teams & Companies',
      href: '/teams-companies',
    },
    {
      text: 'Pricing',
      href: '/pricing',
    },
  ],
};
